import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { readConsent, saveConsent, pageView, applyClarity, applyMeta } from "@/lib/analytics";

export const openCookieSettings = () => window.dispatchEvent(new Event("gf-open-cookies"));

export const RouteTracker = () => {
  const { pathname } = useLocation();
  const last = useRef(pathname);
  useEffect(() => {
    if (last.current === pathname) return;
    last.current = pathname;
    const t = setTimeout(pageView, 50);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
};

const Btn = ({ testId, onClick, children, primary }) => (
  <button data-testid={testId} onClick={onClick}
    className={`w-full rounded-full border-2 border-navy px-5 py-3 text-sm font-bold transition-colors ${primary ? "bg-navy text-sand hover:bg-navy/90" : "bg-sand text-navy hover:bg-foam"}`}>
    {children}
  </button>
);

const Toggle = ({ id, label, desc, checked, onChange, disabled }) => (
  <label htmlFor={id} className="flex items-start justify-between gap-4 rounded-2xl bg-white/70 p-3">
    <span><span className="block text-sm font-bold">{label}</span><span className="block text-xs text-slate-600">{desc}</span></span>
    <Switch id={id} data-testid={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
  </label>
);

export const CookieBanner = () => {
  const [open, setOpen] = useState(() => !readConsent());
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);
  useEffect(() => {
    applyClarity(!!readConsent()?.analytics);
    applyMeta(!!readConsent()?.ads);
    const show = () => { const c = readConsent(); setAnalytics(!!c?.analytics); setAds(!!c?.ads); setManage(false); setOpen(true); };
    window.addEventListener("gf-open-cookies", show);
    return () => window.removeEventListener("gf-open-cookies", show);
  }, []);
  const choose = (a, d) => { saveConsent(a, d); setOpen(false); };
  if (!open) return null;
  return (
    <div data-testid="cookie-banner" role="dialog" aria-label="Cookie settings"
      className="fixed inset-x-3 bottom-3 z-40 mx-auto max-w-2xl rounded-3xl border-2 border-navy bg-sand p-5 shadow-xl sm:inset-x-6">
      <p className="font-display text-lg">Cookies, Grandpa's way</p>
      <p className="mt-1 text-sm text-slate-700">We use essential cookies to run the shop. We use cookies from Google Analytics, Google Ads and Meta (Facebook/Instagram) to understand our site and measure ads, only with your consent. <a href="/privacy" className="underline">Privacy policy</a></p>
      {manage && (
        <div data-testid="cookie-manage" className="mt-4 grid gap-2">
          <Toggle id="cookie-toggle-essential" label="Essential" desc="Needed for the cart, sign-in and checkout. Always on." checked disabled onChange={() => {}} />
          <Toggle id="cookie-toggle-analytics" label="Analytics" desc="Google Analytics 4: pages viewed and how the site is used." checked={analytics} onChange={setAnalytics} />
          <Toggle id="cookie-toggle-ads" label="Advertising" desc="Google Ads and Meta (Facebook/Instagram) pixels to measure and personalise ads." checked={ads} onChange={setAds} />
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Btn testId="cookie-accept-all" primary onClick={() => choose(true, true)}>Accept all</Btn>
        <Btn testId="cookie-reject-all" primary onClick={() => choose(false, false)}>Reject all</Btn>
        {manage
          ? <Btn testId="cookie-save-choices" primary onClick={() => choose(analytics, ads)}>Save choices</Btn>
          : <Btn testId="cookie-manage-button" primary onClick={() => setManage(true)}>Manage</Btn>}
      </div>
    </div>
  );
};
